import { useNavigate } from "react-router-dom";
import { useCommon } from "../context/CommonContext";
import api from "../api/Api";
import { useCallback, useEffect, useMemo, useState } from "react";
import Loading from "./Loading";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "../context/LanguageProvider";

// -----------------------------------------
// Custom hook: useDemographicQuestions
// Fetches demographic questions dynamically
// based on a given surveyId.
// Uses React Query to handle caching, loading,
// error handling, and refetching.
// -----------------------------------------
function useDemographicQuestions(surveyId) {
    return useQuery({
        queryKey: ["demographicsQuestions", surveyId],  // Unique cache key
        queryFn: () => api.fetchdemographicQuestions(surveyId), // Fetcher function
    });
}

const Demographics = () => {
    // -----------------------------------------
    // Pulling shared state & methods from CommonContext:
    // - surveyId, userId: identifies the current survey & participant
    // - iatTestReaction: main IAT test reaction data
    // - practiceReaction: practice test data
    // - explicitResults: explicit question responses from earlier
    // - resetValues: resets context state after submission
    // - continueButtonText: ref object containing localized continue button label
    // -----------------------------------------
    const { surveyId, userId, iatTestReaction, practiceReaction, explicitResults, resetValues, continueButtonText } = useCommon();

    // Pull translation function from language context
    const { translation } = useLanguage();

    // Use custom hook to fetch demographic questions
    // status: "pending" | "success" | "error"
    // fetchStatus: "idle" | "fetching" | "paused"
    // refetch: manual trigger for re-fetching questions
    const { data: initialData, status, error, fetchStatus, refetch } = useDemographicQuestions(surveyId);

    // Local state object to store the user's selected answer for each question
    // Key = question_type, Value = chosen response
    const [selectedResponses, setSelectedResponses] = useState({});

    // React Router navigation hook
    const navigate = useNavigate();

    // -----------------------------------------
    // Memoized question data array
    // Will only recompute when either "status" or "initialData" changes.
    // Ensures no unnecessary re-renders if unrelated state changes occur.
    // -----------------------------------------
    const data = useMemo(() => {
        return status === "success" ? initialData || [] : [];
    }, [status, initialData]);

    // -----------------------------------------
    // Boolean to control whether the "Continue" button should be shown.
    // True if:
    //    1. There are questions
    //    2. The user has answered all of them
    // -----------------------------------------
    const canContinue = useMemo(() => {
        if (data.length === 0) return false;
        return Object.keys(selectedResponses).length === data.length;
    }, [data, selectedResponses]);

    // -----------------------------------------
    // Handles updating the selectedResponses state
    // whenever a user changes an answer in the dropdown.
    // Wrapped in useCallback to avoid re-creating the function
    // unnecessarily on every render.
    // -----------------------------------------
    const handleResponseChange = useCallback((questionType, response) => {
        setSelectedResponses(prev => {
            const updated = { ...prev };
            if (!response || response === translation("Demograhpics_defaultValue")) {
                delete updated[questionType]; // remove if default selected
            } else {
                updated[questionType] = response;
            }
            return updated;
        });
    }, []);

    // -----------------------------------------
    // Submits:
    //  1. IAT test results (main and practice)
    //  2. Demographic responses
    //  3. Explicit results
    //
    // Flow:
    // - Batches IAT submission into a promise,
    //   chaining practice data submission after main results
    // - Executes all submissions concurrently with Promise.all
    // - Resets context values
    // - Navigates to "thank you" page
    //
    // If submission fails, tries again after 2s (temporary retry approach)
    // -----------------------------------------
    const handleButtonClick = async () => {
        // Transform practiceReaction into a specific format expected by API
        const practiceReactiondata = {
            current: practiceReaction.map((data) => {
                return ["X", data, "practice", 1, 1, true, 0, 999];
            })
        };

        try {
            const firstPromise = api.submitIATResults(surveyId, userId, iatTestReaction)
                .then(() => {
                    // Only after submitting main IAT data, submit practice data
                    return api.submitIATResults(surveyId, userId, practiceReactiondata);
                });

            // Run all submissions in parallel
            await Promise.all([
                firstPromise,
                api.submitdemographicsResults(surveyId, userId, selectedResponses),
                api.submitExplicitResults(surveyId, userId, explicitResults)
            ]);

            // Clear stored results in context to prevent stale data
            resetValues();

            // Go to thank-you page
            navigate("/thankyou");
        } catch (error) {
            console.error("Error submitting IAT results:", error);

            // TEMPORARY: retry after 2 seconds
            // (better would be proper error messaging & user retry flow)
            setTimeout(handleButtonClick, 2000);
        }
    };

    // -----------------------------------------
    // If API call for demographic questions 404s (likely meaning no questions configured),
    // skip demographic step and proceed to submission flow automatically.
    // -----------------------------------------
    useEffect(() => {
        if (status === "error" && error && error.message === "Error: 404") {
            handleButtonClick();
        }
    }, [status, error]);

    // -----------------------------------------
    // Render Loading indicator if we're in the middle of fetching
    // -----------------------------------------
    if (fetchStatus === "fetching" || status === "pending") {
        return <Loading />;
    }

    // -----------------------------------------
    // Error handling UI for non-404 errors.
    // Shows either:
    // - An error message from API
    // - Or a network error notice and retry button
    // -----------------------------------------
    if (status === "error" && error.message !== "Error: 404") {
        if (error.message.startsWith("Error:")) {
            return <div>{translation("Error")} {error.message}</div>;
        }
        return (
            <div>
                {translation("Network_error")}
                <button
                    onClick={() => refetch()} // Retry fetching data
                    className="mt-4 px-4 py-2 bg-black-500 text-white rounded hover:bg-black-600 transition duration-200"
                >
                    {translation("Retry")} {/* TODO: ensure translation key exists */}
                </button>
            </div>
        );
    }

    // -----------------------------------------
    // Render the demographic questions form
    // - Each question is displayed with a <select> and its options
    // - Each time user selects, local state is updated
    // - Continue button only appears if user answered all questions
    // -----------------------------------------
    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100 relative">
            {data.map((question, index) => (
                <div key={index} className="mb-7">
                    {/* Question prompt text */}
                    <h4 className="text-2xl font-semibold mt-1 mb-3">{question.question_prompt}</h4>

                    {/* Dropdown for user to select an answer */}
                    <select
                        onChange={(e) => handleResponseChange(question.question_type, e.target.value)}
                        className="border-2 border-gray-300 rounded-md p-1 font-semibold"
                    >
                        {/* Default non-answer placeholder */}
                        <option className="font-semibold" value="">
                            {translation("Demograhpics_defaultValue")}
                        </option>

                        {/* Map question options to <option> elements */}
                        {question.question_options.map((option, optionIndex) => (
                            <option className="font-semibold" key={optionIndex} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
            ))}

            {/* Continue button, fixed to bottom if all answered */}
            {canContinue && 
                <button
                    className="absolute bottom-20 px-6 py-2 bg-black text-white text-xl font-semibold rounded-lg shadow hover:bg-gray-800 transition duration-200 mt-2"
                    onClick={handleButtonClick}
                >
                    {continueButtonText.current}
                </button>
            }            
        </div>
    );
};

export default Demographics;